import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { TableRowData } from './table';

export type TableRowProps<T extends string, AdditionalCallbackFields extends object> = {
  additionalCellContent?: RequireAtLeastOne<
    Record<T, (id: string, additionalFields: AdditionalCallbackFields) => ReactNode>
  >;
  columnOrder: T[];
  contentClassName?: ({ general?: string } & PartialRecord<T, string>) | string;
  fields: TableRowData<T, AdditionalCallbackFields>;
  labelClassName?: ({ general?: string } & PartialRecord<T, string>) | string;
  tdClassName?: ({ general?: string } & PartialRecord<T, string>) | string;
  trClassName?: falsy | string;
};

export type RowPayload<T extends string> = { id: string } & Record<T, number | ReactNode | string>;

export const TableRow = <TColumn extends string, AdditionalCallbackFields extends object = object>({
  fields,
  contentClassName,
  tdClassName,
  trClassName,
  labelClassName,
  columnOrder,
  additionalCellContent,
}: TableRowProps<TColumn, AdditionalCallbackFields>) => {
  return (
    <tr className={twMerge('transition-all', trClassName)}>
      {columnOrder.map(
        (column, i) =>
          columnOrder.includes(column) && (
            <td
              className={twMerge(
                'relative min-w-max',
                typeof tdClassName === 'string'
                  ? tdClassName
                  : twMerge(tdClassName?.[column], tdClassName?.general),
              )}
              key={`${column}_${i}`}
            >
              <div
                className={twMerge(
                  'flex h-[72px] items-center gap-8 px-2',
                  typeof contentClassName === 'string'
                    ? contentClassName
                    : twMerge(contentClassName?.[column], contentClassName?.general),
                )}
              >
                <span
                  className={twMerge(
                    'text-xs font-[450] text-gray-700',
                    typeof labelClassName === 'string'
                      ? labelClassName
                      : twMerge(labelClassName?.[column], labelClassName?.general),
                  )}
                >
                  {typeof fields[column] === 'function'
                    ? fields[column](fields.id, fields) || null
                    : fields[column]}
                </span>
                {additionalCellContent?.[column]?.(fields.id, fields)}
              </div>
            </td>
          ),
      )}
    </tr>
  );
};
